import { SquareLogoElement } from "../destinations/types";
import SvgOneTrust from "../components/icons/svg/logos/OneTrust";
import SvgOsano from "../components/icons/svg/logos/Osano";
import SvgFreshpaint from "../components/icons/svg/logos/Freshpaint";
import { SaaSDestination } from "../destinations/destinations";

export type ConsentManagementSlug = "osano" | "onetrust" | "freshpaint";

export type ConsentManagementKey = "Osano" | "OneTrust" | "Freshpaint";

type ConsentManagementIntegrationData = {
  slug: ConsentManagementSlug;
  name: string;
  key: ConsentManagementKey;
  logoSquare: SquareLogoElement | null;
  // docsURL is optional only because we haven't added the URL for each
  // destination yet. In theory every destination should have a docs page.
  docsURL?: string;
  configBodyHelp?: JSX.Element | string;
  allowsCustomCategories: boolean;
  defaultConsentCategories: Record<string, string>;
  defaultCategoryDescriptions?: Record<string, string>;
  experimental?: boolean;
  entitlementKey: string;
};

export class ConsentManagementIntegration {
  slug: ConsentManagementSlug;
  name: string;
  key: ConsentManagementKey;
  logoSquare: SquareLogoElement | null;
  configBodyHelp: JSX.Element | string;
  docsURL: string;
  allowsCustomCategories: boolean;
  defaultConsentCategories: Record<string, string>;
  defaultCategoryDescriptions: Record<string, string>;
  experimental: boolean;
  entitlementKey: string;

  constructor(props: ConsentManagementIntegrationData) {
    const {
      slug,
      name,
      key,
      logoSquare,
      docsURL,
      configBodyHelp,
      allowsCustomCategories,
      defaultConsentCategories,
      defaultCategoryDescriptions,
      experimental,
      entitlementKey,
    } = props;

    this.slug = slug;
    this.name = name;
    this.key = key;

    this.logoSquare = logoSquare || null;
    this.docsURL =
      docsURL ||
      "https://documentation.freshpaint.io/integrations/consent-management";

    this.configBodyHelp = configBodyHelp || "";

    this.allowsCustomCategories = allowsCustomCategories;
    this.defaultConsentCategories = defaultConsentCategories;
    this.defaultCategoryDescriptions = defaultCategoryDescriptions || {};
    this.experimental = experimental || false;
    this.entitlementKey = entitlementKey;
  }

  configPageUrl() {
    return `/consent-management/${this.slug}`;
  }

  // getDefaultConsentCategoryForDest returns the hard-coded default consent category for a
  // given destination if it exists, and empty string if it doesn't. It's not always
  // clear what the default category should be, so this is a best effort attempt.
  getDefaultConsentCategoryForDest(dest: SaaSDestination) {
    const fpConsentCategory: FPConsentManagementCategory | null =
      dest.defaultConsentManagementCategory;
    if (fpConsentCategory) {
      return fpConsentManagementCategoryMap[this.slug][fpConsentCategory];
    }
    return "";
  }
}

export const consentManagementIntegrations: Record<
  ConsentManagementSlug,
  ConsentManagementIntegration
> = {
  osano: new ConsentManagementIntegration({
    slug: "osano",
    name: "Osano",
    key: "Osano",
    logoSquare: SvgOsano,
    docsURL:
      "https://documentation.freshpaint.io/integrations/consent-management/osano",
    allowsCustomCategories: false,
    defaultConsentCategories: {
      ANALYTICS: "Analytics",
      MARKETING: "Marketing",
      PERSONALIZATION: "Personalization",
      ESSENTIAL: "Essential",
    },
    experimental: false,
    entitlementKey: "Osano",
  }),
  onetrust: new ConsentManagementIntegration({
    slug: "onetrust",
    name: "OneTrust",
    key: "OneTrust",
    logoSquare: SvgOneTrust,
    docsURL:
      "https://documentation.freshpaint.io/integrations/consent-management/onetrust",
    allowsCustomCategories: true,
    defaultConsentCategories: {
      C0001: "Essential",
      C0002: "Performance", // analytics
      C0003: "Functional", // personalization
      C0004: "Targeting", // marketing
      C0005: "Social Media",
    },
    experimental: false,
    entitlementKey: "OneTrust",
  }),
  freshpaint: new ConsentManagementIntegration({
    slug: "freshpaint",
    name: "Freshpaint",
    key: "Freshpaint",
    logoSquare: SvgFreshpaint,
    docsURL:
      "https://documentation.freshpaint.io/integrations/destinations/consent-management/freshpaint-consent-manager",
    allowsCustomCategories: true,
    defaultConsentCategories: {
      analytics: "Analytics",
      marketing: "Marketing",
      personalization: "Personalization",
      essential: "Essential",
    },
    defaultCategoryDescriptions: {
      essential:
        "These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt out of these cookies.",
      analytics:
        "These cookies help us understand how visitors interact with our website. This allows us to improve our site's performance and user experience.",
      marketing:
        "These cookies allow us to deliver advertising that is more relevant to you and your interests. They do not directly store personal information, but are based on uniquely identifying your browser and internet device. If you do not allow these cookies, you will experience less targeted advertising.",
      personalization:
        "These cookies enable enhanced functionality and personalization, such as remembering your preferences, language settings, and region.",
    },
    experimental: false,
    entitlementKey: "Freshpaint Consent Manager",
  }),
};

// Right now, Freshpaint really only has apps that are either "analytics" or "marketing"
// More might be added later. We should have a mapping from any internal categories
// to the categories that each of our consent management integrations have
export type FPConsentManagementCategory = "analytics" | "marketing";
type FPConsentManagementCategoryMap = {
  [slug in ConsentManagementSlug]: {
    [slug in FPConsentManagementCategory]: string;
  };
};

const fpConsentManagementCategoryMap: FPConsentManagementCategoryMap = {
  osano: {
    analytics: "ANALYTICS",
    marketing: "MARKETING",
  },
  onetrust: {
    analytics: "C0002",
    marketing: "C0004",
  },
  freshpaint: {
    analytics: "analytics",
    marketing: "marketing",
  },
};
